import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ukDate } from "./QuoteForm";
import { CalendarCheck, Mail, MailX, Image as ImageIcon, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  enquiry_confirmation: "Enquiry confirmation",
  quote_followup: "Quote follow-up",
  booking_confirmation: "Booking confirmation",
  appointment_reminder: "Appointment reminder",
  review_request: "Review request",
};

const AdminPhoto = ({ id }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    axios.get(`${API}/admin/photos/${id}`, { withCredentials: true, responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(""));
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  if (src === null) return <div className="h-24 w-24 animate-pulse rounded-lg bg-slate-200" />;
  if (!src) return <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-slate-100 text-xs text-slate-400">Unavailable</div>;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" data-testid={`admin-quote-photo-${id}`}>
      <img src={src} alt="Customer property" className="h-24 w-24 rounded-lg border border-slate-200 object-cover" />
    </a>
  );
};

const BookingForm = ({ quote, onBooked }) => {
  const [date, setDate] = useState(quote.appointmentDate || quote.preferredDates?.[0] || "");
  const [time, setTime] = useState(quote.appointmentTime || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const book = async () => {
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/admin/quotes/${quote.id}/booking`, { date, time }, { withCredentials: true });
      onBooked();
    } catch (err) {
      setError(err?.response?.status === 401 ? "Session expired - please log in again" : err?.response?.data?.detail || "Booking failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`admin-booking-form-${quote.id}`}>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm" data-testid={`admin-booking-date-${quote.id}`} />
      <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm" data-testid={`admin-booking-time-${quote.id}`} />
      <button onClick={book} disabled={!date || busy} className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50" data-testid={`admin-booking-submit-${quote.id}`}>
        <CalendarCheck className="h-4 w-4" /> {quote.appointmentDate ? "Update booking & email" : "Book & email customer"}
      </button>
      {error && <p className="w-full text-xs font-medium text-red-600" data-testid={`admin-booking-error-${quote.id}`}>{error}</p>}
    </div>
  );
};

const AdminQuoteExtras = ({ quote }) => {
  const [comms, setComms] = useState([]);
  const [booking, setBooking] = useState(quote.appointmentLabel);
  const loadComms = useCallback(() => {
    axios.get(`${API}/admin/quotes/${quote.id}/communications`, { withCredentials: true }).then((r) => setComms(r.data)).catch(() => {});
  }, [quote.id]);
  useEffect(() => { loadComms(); }, [loadComms]);

  const onBooked = () => {
    axios.get(`${API}/quotes`, { withCredentials: true }).then((r) => setBooking(r.data.find((q) => q.id === quote.id)?.appointmentLabel)).catch(() => {});
    window.dispatchEvent(new Event("nw-quotes-refresh"));
    setTimeout(loadComms, 3000);
  };

  return (
    <div className="mt-6 grid gap-6 border-t border-slate-200 pt-6 md:grid-cols-3" data-testid={`admin-quote-extras-${quote.id}`}>
      <div className="space-y-2 text-sm">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Booking</h4>
        <p className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" /> {quote.postcode || "No postcode"}</p>
        <p><span className="text-slate-500">Preferred:</span> {quote.preferredDates?.map(ukDate).join(" or ") || "-"}</p>
        <p data-testid={`admin-appointment-${quote.id}`}><span className="text-slate-500">Booked for:</span> <strong>{booking || "Not booked"}</strong></p>
        <p><span className="text-slate-500">Review emails:</span> {quote.reviewConsent ? "Allowed" : "Not allowed"}</p>
        <BookingForm quote={quote} onBooked={onBooked} />
      </div>
      <div className="space-y-2">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><ImageIcon className="h-3.5 w-3.5" /> Photos ({quote.photoIds?.length || 0})</h4>
        <div className="flex flex-wrap gap-2">{(quote.photoIds || []).map((id) => <AdminPhoto key={id} id={id} />)}</div>
      </div>
      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer emails</h4>
        {comms.length === 0 ? <p className="text-sm text-slate-400">No emails yet</p> : (
          <ul className="space-y-1.5" data-testid={`admin-comms-${quote.id}`}>
            {comms.map((c) => (
              <li key={c.id} className="flex items-start gap-2 text-sm">
                {c.sent ? <Mail className="mt-0.5 h-4 w-4 text-green-500" /> : <MailX className="mt-0.5 h-4 w-4 text-red-400" />}
                <span><strong>{KIND_LABELS[c.kind] || c.kind}</strong> <span className="text-slate-500">{new Date(c.at).toLocaleString("en-GB")}</span></span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminQuoteExtras;
